/**
 * Private events (BON-518) — corporate and other whole-event bookings that are
 * never on public sale.
 *
 * Row shape mirrors `PrivateEventListEntry` in
 * backend/src/services/private-events.service.ts — keep in sync.
 */
import { apiFetch } from '../lib/apiFetch';

import { RegionNotFoundError } from './region-not-found-error';

/** Where an event happens: a partner venue, a client's address, not yet decided,
 *  or an imported event whose location FareHarbor never held. */
export type PrivateEventLocationKind = 'venue' | 'client_site' | 'tbd' | 'unrecorded';

/** Authoritative lifecycle, mirroring the `PrivateEventLifecycle` enum. */
export type PrivateEventLifecycle = 'CONFIRMED' | 'COMPLETED' | 'CANCELLED';

export interface PrivateEvent {
  id: string;
  clientName: string;
  lifecycle: PrivateEventLifecycle;
  /** UTC instant — sorting and comparison only, never display. */
  startsAt: string;
  /** Venue wall clock, naive (no trailing `Z`) — display only, never parsed as UTC. */
  localStartsAt: string | null;
  /** Null when the event is not at a partner venue. */
  venueName: string | null;
  /** Which of the four location shapes this event has. Label from this: a
   *  to-be-determined and an unrecorded location both have no place to show. */
  locationKind: PrivateEventLocationKind;
  /** Client-site fields — set only for a venueless event, else null. */
  locationName: string | null;
  city: string | null;
  state: string | null;
  regionId: string | null;
  headcount: number;
  /** Hand-entered and unreconciled. Must be labelled as an estimate wherever shown. */
  dealValueCents: number | null;
  hubspotDealId: string | null;
  ownerUserId: string | null;
  isStaffed: boolean;
  /** Whether the client can be reached: an email or a phone is on file. */
  hasContact: boolean;
  /** Imported from FareHarbor, so its location and crew may never have been recorded. */
  migrated: boolean;
}

/** Fields common to both create modes. */
interface CreatePrivateEventCommon {
  workshopTypeId: string;
  /** Wall clock as typed, with NO zone. Venue mode derives the instant through
   *  the venue's timezone; client-site mode through the typed/resolved one. */
  startsAt: string;
  endsAt: string;
  clientName: string;
  headcount: number;
  dealValueCents?: number | null;
  hubspotDealId?: string | null;
  ownerUserId?: string | null;
  dealOriginatorUserId?: string | null;
  nationalCoordinatorUserId?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  addOns?: string[];
  notes?: string | null;
}

/** Mode 1: the event happens at an existing partner venue. */
export interface CreatePrivateEventVenueInput extends CreatePrivateEventCommon {
  venueId: string;
}

/**
 * Mode 2: the event happens at a client's typed address. Carries its own host
 * region (for scope + visibility) and a resolved IANA timezone (for deriving
 * the instant) — no `venueId`.
 */
export interface CreatePrivateEventClientSiteInput extends CreatePrivateEventCommon {
  regionId: string;
  locationName: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
  timeZone: string;
}

/** Mode 3: the location is not decided yet. Only the region and the time zone
 *  the date and time are read in are known. */
export interface CreatePrivateEventTbdInput extends CreatePrivateEventCommon {
  locationTbd: true;
  regionId: string;
  timeZone: string;
}

/** Exactly one mode — the server 400s on any mix, or none. */
export type CreatePrivateEventInput =
  | CreatePrivateEventVenueInput
  | CreatePrivateEventClientSiteInput
  | CreatePrivateEventTbdInput;

export class PrivateEventValidationError extends Error {
  constructor(
    public readonly fieldErrors: Record<string, string>,
    message: string,
  ) {
    super(message);
    this.name = 'PrivateEventValidationError';
  }
}

const NOT_FOUND_MESSAGE =
  'We couldn’t find something you picked. It may have been removed — choose it again.';
const FORBIDDEN_MESSAGE = 'You don’t have permission to create a private event in that region.';

export class PrivateEventForbiddenError extends Error {
  constructor(serverMessage?: string) {
    super(serverMessage || FORBIDDEN_MESSAGE);
    this.name = 'PrivateEventForbiddenError';
  }
}

export interface PrivateEventList {
  privateEvents: PrivateEvent[];
  /** The regions the list is drawn from. Null when the caller sees every region. */
  visibleRegionIds: string[] | null;
}

export async function fetchPrivateEvents(regionSlug?: string): Promise<PrivateEventList> {
  const query = regionSlug ? `?${new URLSearchParams({ region: regionSlug })}` : '';
  const res = await apiFetch(`/api/v1/private-events${query}`);
  if (res.status === 404 && regionSlug) throw new RegionNotFoundError(regionSlug);
  if (res.status === 400)
    throw new Error('That region filter couldn’t be read. Pick a region from the list.');
  if (res.status === 403) throw new Error('You don’t have permission to view private events.');
  if (!res.ok) throw new Error('Private events couldn’t be loaded. Try again in a moment.');
  return (await res.json()) as PrivateEventList;
}

/** One crew member on a private event's detail read. Mirrors
 *  `PrivateEventCrewMember` in backend/src/services/private-events.detail.ts. */
export interface PrivateEventCrewMember {
  userId: string;
  name: string;
  position: string;
}

/** A user named on a private event. */
export interface PrivateEventPerson {
  id: string;
  name: string;
}

/**
 * The private-event detail read — everything the detail page's fields and
 * crew card need. Mirrors `PrivateEventDetailDto` in
 * backend/src/services/private-events.detail.ts — keep in sync.
 */
export interface PrivateEventDetail {
  id: string;
  clientName: string;
  lifecycle: PrivateEventLifecycle;
  /** UTC instant — sorting and comparison only, never display. */
  startsAt: string;
  /** Venue wall clock, naive (no trailing `Z`) — display only, never parsed as UTC. */
  localStartsAt: string | null;
  /** Null when the event is not at a partner venue. */
  venueName: string | null;
  locationKind: PrivateEventLocationKind;
  regionId: string | null;
  headcount: number;
  /** Hand-entered and unreconciled. Must be labelled as an estimate wherever shown. */
  dealValueCents: number | null;
  hubspotDealId: string | null;
  ownerUserId: string | null;
  /** Imported from FareHarbor, so its location and crew may never have been recorded. */
  migrated: boolean;
  /** UTC instant — comparison only, never display. */
  endsAt: string;
  /** Venue wall clock, naive. */
  localEndsAt: string | null;
  workshopTypeId: string;
  workshopTypeName: string;
  venueId: string | null;
  /** The zone the wall clocks are read in: the venue's, else the event's own. */
  timeZone: string | null;
  owner: PrivateEventPerson | null;
  nationalCoordinator: PrivateEventPerson | null;
  dealOriginator: PrivateEventPerson | null;
  depositReceived: boolean;
  hubspotPaymentId: string | null;
  /** Client-site fields — set only for a venueless event, else null. */
  locationName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  addOns: string[];
  notes: string | null;
  crew: PrivateEventCrewMember[];
}

/**
 * What a teacher crewed on the event gets back when they cannot read private
 * events: the detail without the deal, the deposit, the HubSpot records and the
 * people on the deal. Mirrors `PrivateEventCrewViewDto` in
 * backend/src/lib/private-event-crew-view.ts.
 */
export type PrivateEventCrewView = Omit<
  PrivateEventDetail,
  | 'dealValueCents'
  | 'depositReceived'
  | 'hubspotDealId'
  | 'hubspotPaymentId'
  | 'owner'
  | 'ownerUserId'
  | 'nationalCoordinator'
  | 'dealOriginator'
>;

/** Thrown on a 404 so the detail page can render "not found" distinctly from
 *  a generic load failure — the guard's not-found is genuinely indistinguishable
 *  from an out-of-scope caller's, so the page only ever says "not found". */
export class PrivateEventNotFoundError extends Error {
  constructor() {
    super('Private event not found');
    this.name = 'PrivateEventNotFoundError';
  }
}

async function fetchPrivateEventBody<T>(id: string): Promise<T> {
  const res = await apiFetch(`/api/v1/private-events/${id}`);
  if (res.status === 404) throw new PrivateEventNotFoundError();
  if (!res.ok) {
    throw new Error(`Failed to fetch private event: ${res.status}`);
  }
  const data = (await res.json()) as { privateEvent: T };
  return data.privateEvent;
}

export function fetchPrivateEvent(id: string): Promise<PrivateEventDetail> {
  return fetchPrivateEventBody<PrivateEventDetail>(id);
}

export function fetchPrivateEventCrewView(id: string): Promise<PrivateEventCrewView> {
  return fetchPrivateEventBody<PrivateEventCrewView>(id);
}

export async function createPrivateEvent(input: CreatePrivateEventInput): Promise<{ id: string }> {
  const res = await apiFetch('/api/v1/private-events', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
      fieldErrors?: Record<string, string>;
    };
    if (res.status === 400 && err.fieldErrors) {
      throw new PrivateEventValidationError(err.fieldErrors, err.message ?? 'Validation failed');
    }
    if (res.status === 404 && err.fieldErrors) {
      throw new PrivateEventValidationError(err.fieldErrors, NOT_FOUND_MESSAGE);
    }
    if (res.status === 403) throw new PrivateEventForbiddenError(err.message);
    throw new Error(err.error || err.message || `Failed to create private event: ${res.status}`);
  }
  return res.json();
}

/**
 * The ten add-ons the business sells, in the order the design lists them.
 * Ids match the server's validated set.
 */
export const PRIVATE_EVENT_ADD_ONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'trays', label: 'Trays' },
  { id: 'fertilizer', label: 'Fertilizer' },
  { id: 'tray-fertilizer-combo', label: 'Tray + fertilizer combo' },
  { id: 'mame-kit', label: 'Mame kit' },
  { id: 'willow-pot-upgrade', label: 'Willow pot upgrade' },
  { id: 'bonsai-shears', label: 'Bonsai shears' },
  { id: 'premium-bonsai-shears', label: 'Premium bonsai shears' },
  { id: 'gift-mame', label: 'Add-on gift mame' },
  { id: 'airline-packaging', label: 'Airline-friendly packaging' },
  { id: 'diy-kit', label: 'DIY kit' },
];

/**
 * Past means the event has already started. Derived from the UTC instant, not
 * the wall clock: `localStartsAt` carries no zone, so comparing it to `now`
 * would be wrong by the venue's offset.
 */
export function isPastPrivateEvent(event: PrivateEvent, now: Date = new Date()): boolean {
  return new Date(event.startsAt).getTime() < now.getTime();
}
